import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Optional, Output, ContentChild, HostListener, NgModule } from '@angular/core';
import { DatePipe, CommonModule } from '@angular/common';
import { Subject, timer, interval, fromEvent, of } from 'rxjs';
import { startWith, tap, switchMap, filter, map, distinctUntilChanged, catchError, delay } from 'rxjs/operators';
import * as i1 from '@roosevelt/common-ui-lib/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@roosevelt/common-ui-lib/core';
import { DialogComponent, DialogModule } from '@roosevelt/common-ui-lib/dialog';
import { IconComponent, IconModule } from '@roosevelt/common-ui-lib/icon';
import { ButtonComponent, ButtonModule } from '@roosevelt/common-ui-lib/button';
import { SelectComponent, SelectModule } from '@roosevelt/common-ui-lib/select';
import { BannerComponent, BannerModule } from '@roosevelt/common-ui-lib/banner';
import { RouterLink, RouterModule } from '@angular/router';
import * as i2 from '@angular/platform-browser';

/* eslint-disable max-len */
class TimeoutConfig {
    constructor() {
        this.inactivityTimeout = 0;
        this.reset$ = new Subject();
        this.tryLoggingOut$ = new Subject();
        this.countdownTotal = 2 * 60 * 1000;
        this.countdown$ = new Subject();
        this.activityTimerSub = undefined;
        this.clickHandlerSub = undefined;
        this.innerTimerSub = undefined;
    }
}
/**
 * Sticky Header Component
 *
 * @description this is a content component for the header. it will stick to the top of the page once you pass the header scrolling
 * @example
 * <!-- rs-doc {link: 'Example of Sticky Header'} -->
 * <rs-header appName="Test App" [inactivityTimeout]="timeout" [adminPlans]="[{label:'All Commercial', image: 'all', value: 1, group:'Commercial'}, {label:'DDMI', image:'ddmi', value: 2, group:'Commercial'}, {label:'DDAZ', image:'ddaz', value: 3, group:'Commercial'}, {label:'DDMN', image:'ddmn', value: 4, group:'Commercial'}, {label:'DDKY', image:'ddky', value: 5, group:'Commercial'}, {label:'DDNM', image:'ddnm', value: 6, group:'Commercial'}, {label:'DDNC', image:'ddnc', value: 7, group:'Commercial'}, {label:'FS', image:'fs', value: 8, group:'Federal'}]" userName="Brandon Staley">
 *  <rs-sticky-header>
 *  <span class="app-name">My Awesome App</span>
 * </rs-sticky-header>
 * </rs-header>
 * <div style="margin-top: 500px">ignore, just for scrolling effect</div>
 * <!-- rs-doc {link: 'Example of Sticky Header with the banner'} -->
 * <rs-header appName="Test App" showBanner [inactivityTimeout]="timeout" [adminPlans]="[{label:'DDAZ', image:'ddaz', value: 3, group:'Commercial'}]" userName="Brandon Staley">
 *  <rs-sticky-header>
 *  <span class="app-name">My Awesome App</span>
 * </rs-sticky-header>
 * </rs-header>
 * <div style="margin-top: 1000px">ignore, just for scrolling effect</div>
 */
class StickyHeaderComponent {
    /** Use this if you want to hide the adminplan selector in the sticky header */
    get hideAdminPlanSelection() {
        return this._hideAdminPlanSelection;
    }
    set hideAdminPlanSelection(value) {
        this._hideAdminPlanSelection = coerceBooleanProperty(value);
    }
    /** Allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        /** emits out the value of the selected plan when a users chooses a new admin plan */
        this.planSelected = new EventEmitter();
        /** a list of admin plans/payers the user has access to */
        this.adminPlans = [];
        /** use this if you want to set the users default */
        this.selectedAdminPlan = '';
        this.region = '';
        this.showBanner = false;
        this._color = 'primary';
        this._hideAdminPlanSelection = false;
    }
    ngOnInit() {
        this.color = this.color;
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    // eslint-disable-next-line @typescript-eslint/member-ordering, @typescript-eslint/naming-convention,no-underscore-dangle,id-blacklist,id-match
    static { this.ngAcceptInputType_disabled = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: StickyHeaderComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: StickyHeaderComponent, isStandalone: true, selector: "rs-sticky-header", inputs: { hideAdminPlanSelection: "hideAdminPlanSelection", color: "color" }, ngImport: i0, template: `
  @if (showBanner) {
    <rs-banner></rs-banner>
  }
  <header>
    <div class="spacer">
      <ng-content></ng-content>
    </div>
    @if (adminPlans.length === 1 && !hideAdminPlanSelection) {
      <span [class]="'selected-' + adminPlans[0].value + ' single-plan'" ><span [class]="$any(adminPlans[0]).image"></span> {{selectedAdminPlan}}</span>
    }
    @if (adminPlans.length > 1 && !hideAdminPlanSelection) {
      <rs-select [color]="color" [items]="adminPlans" placeholder="Admin Plans"
        [class]="selectedAdminPlan ? ('selected-' + selectedAdminPlan) : ''"
      [selectValue]="selectedAdminPlan" (change)="planSelected.emit(this.selectedAdminPlan = $any($event))"></rs-select>
    }
  </header>
  `, isInline: true, styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host header{position:absolute;left:0;right:0;display:flex;flex-wrap:wrap;padding:10px 20px;align-items:center}:host header ::ng-deep .logo{border-right:2px gray solid;padding:0 20px}:host header ::ng-deep .logo img{max-height:30px}:host header ::ng-deep .spacer{flex:1}:host header ::ng-deep .app-name{padding:0 20px;font-size:1.75em}:host header ::ng-deep .user-name{padding:0 30px}:host header ::ng-deep .single-plan{border:1px solid #222;border-radius:4px;display:flex;margin:4px 0;min-height:42px;min-width:160px;align-items:center;padding:0 10px;justify-content:flex-start}:host header ::ng-deep rs-select{min-width:160px}:host ::ng-deep rs-select>.form-group{margin-bottom:4px!important}.env-banner-tst,.env-banner-uat,.env-banner-local{width:100%;color:#fff;text-transform:uppercase;margin:0}.env-banner-tst.env-banner-list,.env-banner-uat.env-banner-list,.env-banner-local.env-banner-list{font-weight:700;display:flex;justify-content:space-around;list-style:none;padding:0}.env-banner-tst.env-banner-list li,.env-banner-uat.env-banner-list li,.env-banner-local.env-banner-list li{font-size:1em;color:#fff}.env-banner-tst{background-color:#0c0}.env-banner-uat{background-color:#d70c11}.env-banner-local{background-color:#0040ff}\n"], dependencies: [{ kind: "component", type: BannerComponent, selector: "rs-banner" }, { kind: "component", type: SelectComponent, selector: "rs-select", inputs: ["autocomplete", "label", "placeholder", "validation", "errors", "appendTo", "hideErrorMessage", "id", "multiple", "searchable", "clearable", "items", "selectValue", "tabindex", "disabled", "color"], outputs: ["change", "close"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: StickyHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-sticky-header', template: `
  @if (showBanner) {
    <rs-banner></rs-banner>
  }
  <header>
    <div class="spacer">
      <ng-content></ng-content>
    </div>
    @if (adminPlans.length === 1 && !hideAdminPlanSelection) {
      <span [class]="'selected-' + adminPlans[0].value + ' single-plan'" ><span [class]="$any(adminPlans[0]).image"></span> {{selectedAdminPlan}}</span>
    }
    @if (adminPlans.length > 1 && !hideAdminPlanSelection) {
      <rs-select [color]="color" [items]="adminPlans" placeholder="Admin Plans"
        [class]="selectedAdminPlan ? ('selected-' + selectedAdminPlan) : ''"
      [selectValue]="selectedAdminPlan" (change)="planSelected.emit(this.selectedAdminPlan = $any($event))"></rs-select>
    }
  </header>
  `, standalone: true, imports: [BannerComponent, SelectComponent], styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host header{position:absolute;left:0;right:0;display:flex;flex-wrap:wrap;padding:10px 20px;align-items:center}:host header ::ng-deep .logo{border-right:2px gray solid;padding:0 20px}:host header ::ng-deep .logo img{max-height:30px}:host header ::ng-deep .spacer{flex:1}:host header ::ng-deep .app-name{padding:0 20px;font-size:1.75em}:host header ::ng-deep .user-name{padding:0 30px}:host header ::ng-deep .single-plan{border:1px solid #222;border-radius:4px;display:flex;margin:4px 0;min-height:42px;min-width:160px;align-items:center;padding:0 10px;justify-content:flex-start}:host header ::ng-deep rs-select{min-width:160px}:host ::ng-deep rs-select>.form-group{margin-bottom:4px!important}.env-banner-tst,.env-banner-uat,.env-banner-local{width:100%;color:#fff;text-transform:uppercase;margin:0}.env-banner-tst.env-banner-list,.env-banner-uat.env-banner-list,.env-banner-local.env-banner-list{font-weight:700;display:flex;justify-content:space-around;list-style:none;padding:0}.env-banner-tst.env-banner-list li,.env-banner-uat.env-banner-list li,.env-banner-local.env-banner-list li{font-size:1em;color:#fff}.env-banner-tst{background-color:#0c0}.env-banner-uat{background-color:#d70c11}.env-banner-local{background-color:#0040ff}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { hideAdminPlanSelection: [{
                type: Input
            }], color: [{
                type: Input
            }] } });
/**
 * Header Component
 *
 * @description the header component is on every application. it shows app name, user name and sign out
 * @example
 * <!-- rs-doc {link: 'Example of Nested Admin Plans'} -->
 * <button rs-raised-button *ngIf="!timeout" (click)="timeout = 130000">Set Inactive Timeout (130s)</button>
 * <rs-header useAdminPlanWarning appName="Test App" [userNameLink]="{route:'/'}" [inactivityTimeout]="timeout" [adminPlans]="[{label:'All Commercial', image: 'all', value: 1, group:'Commercial'}, {label:'DDMI', image:'ddmi', value: 2, group:'Commercial'}, {label:'DDAZ', image:'ddaz', value: 3, group:'Commercial'}, {label:'DDMN', image:'ddmn', value: 4, group:'Commercial'}, {label:'DDKY', image:'ddky', value: 5, group:'Commercial'}, {label:'DDNM', image:'ddnm', value: 6, group:'Commercial'}, {label:'DDNC', image:'ddnc', value: 7, group:'Commercial'}, {label:'FS', image:'fs', value: 8, group:'Federal'}]" userName="Brandon Staley"></rs-header>
 * @example
 * <!-- rs-doc {link: 'Example of One Admin Plan With All Colors'} -->
 * <rs-header appName="Test App" color="basic" [adminPlans]="[{label:'DDMI', image:'ddmi', value: 2, group:'Commercial'}]" [userNameLink]="{text:'Brandon Staley'}"></rs-header>
 * <rs-header appName="Test App" [adminPlans]="[{label:'DDAZ', image:'ddaz', value: 3, group:'Commercial'}]" [userNameLink]="{text:'Brandon Staley'}"></rs-header>
 * <rs-header appName="Test App" color="secondary" [adminPlans]="[{label:'DDKY', image:'ddky', value: 5, group:'Commercial'}]" [userNameLink]="{text:'Brandon Staley'}"></rs-header>
 */
class HeaderComponent {
    static { this.timeoutConfig = new TimeoutConfig(); }
    /** if you would like to warn the user of data loss when switching admin plans */
    get useAdminPlanWarning() {
        return this._useAdminPlanWarning;
    }
    set useAdminPlanWarning(value) {
        this._useAdminPlanWarning = coerceBooleanProperty(value);
    }
    /** a list of admin plans/payers the user has access to can use any form of ILabelValue */
    get adminPlans() {
        return this._adminPlans;
    }
    set adminPlans(value) {
        this._adminPlans = value;
        if (this.stickyHeader) {
            this.stickyHeader.adminPlans = this.adminPlans;
        }
    }
    /** use this if you want to set the users default */
    get selectedAdminPlan() {
        return this._selectedAdminPlan;
    }
    set selectedAdminPlan(value) {
        this._selectedAdminPlan = value;
        if (this.stickyHeader) {
            this.stickyHeader.selectedAdminPlan = this.selectedAdminPlan;
        }
    }
    /** in ms a timeout before a dialog shows with an inactive message. if not set will be defaulted to 30 mins */
    get inactivityTimeout() {
        return this._inactivityTimeout;
    }
    set inactivityTimeout(value) {
        this._inactivityTimeout = coerceNumberProperty(value);
        const { subscriptions, countdown$, shouldLogOut$, inactivityDisplay, countdownTotalInSeconds } = HeaderComponent.setUpTimeout(this._inactivityTimeout);
        this.subscriptions = [
            ...this.subscriptions,
            ...subscriptions,
            countdown$.subscribe(x => this.countdown = x),
            shouldLogOut$.subscribe(() => this.tryLoggingOut())
        ];
        this.inactivityDisplay = inactivityDisplay;
        this.countdownTotalInSeconds = countdownTotalInSeconds;
    }
    /** if you use this attribute, the header will show a banner displaying local, test or uat. if it finds the region(BAC) to be prd it will not show the banner */
    get showBanner() {
        return this._showBanner;
    }
    set showBanner(value) {
        this._showBanner = coerceBooleanProperty(value);
    }
    /** Allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    constructor(elementRef, config, sanitizer, dropInConfig) {
        this.elementRef = elementRef;
        this.config = config;
        this.sanitizer = sanitizer;
        this.dropInConfig = dropInConfig;
        /** emits out the value of the selected plan when a users chooses a new admin plan */
        this.planSelected = new EventEmitter();
        /** emits when the user chooses to logout */
        this.logout = new EventEmitter();
        /** allows users to click their name and navigate somewhere */
        this.userNameLink = undefined;
        /** configures a logo with route(internal) or url(external). use imgSrc to define your image */
        this.logo = undefined;
        /** configures an icon with route to the help page. use imgSrc to define your image */
        this.helpIcon = undefined;
        /** name of the application */
        this.appName = '';
        /**
         * alias of a user
         *
         * @deprecated in future versions this will be removed in favor of userNameLink.text
         */
        this.userName = '';
        this.stickyHeader = undefined;
        this.nextSelectedAdminPlan = '';
        this.nextSelectedAdminPlanDisplay = '';
        this.selectedAdminPlanDisplay = '';
        this.showAdminPlanWarning = false;
        this.inactivityDisplay = new Date(0, 0, 0, 0, 0, 0, 0);
        this.countdownTotalInSeconds = 0;
        this.showSticky = false;
        this.refreshUrl = undefined;
        this.countdown = 0;
        this.scrollSub = undefined;
        this.subscriptions = [];
        this._useAdminPlanWarning = false;
        this._color = 'primary';
        this._showBanner = false;
        this._adminPlans = [];
        this._selectedAdminPlan = '';
        this._inactivityTimeout = 0;
    }
    static setUpTimeout(inactivityTimeout) {
        const inactivityDisplay = new Date(0, 0, 0, 0, 0, 0, 0);
        const countdownTotalInSeconds = (this.timeoutConfig.countdownTotal / 1000);
        // queue for refreshing, activity watcher, inactivity timer, refresh timer
        if (inactivityTimeout && !this.timeoutConfig.activityTimerSub) {
            if (this.timeoutConfig.countdownTotal < inactivityTimeout) {
                inactivityDisplay.setSeconds((inactivityTimeout - this.timeoutConfig.countdownTotal) / 1000);
                this.timeoutConfig.activityTimerSub = this.timeoutConfig.reset$.pipe(startWith(0), tap(() => { if (this.timeoutConfig.innerTimerSub) {
                    this.timeoutConfig.innerTimerSub.unsubscribe();
                    this.timeoutConfig.innerTimerSub = undefined;
                    this.timeoutConfig.countdown$.next(0);
                } }), switchMap(() => timer(0, inactivityTimeout - this.timeoutConfig.countdownTotal).pipe(filter(x => !!x), map(() => 'expiring'))), filter(x => x === 'expiring')).subscribe(() => {
                    if (!this.timeoutConfig.innerTimerSub) {
                        this.timeoutConfig.innerTimerSub = interval(1000).subscribe(i => {
                            this.timeoutConfig.countdown$.next(i + 1);
                            const lastActivityAt = localStorage.getItem('last-activity-at');
                            if (i === (countdownTotalInSeconds - 1) || (lastActivityAt && (+lastActivityAt + inactivityTimeout) < new Date().valueOf())) {
                                this.timeoutConfig.reset$.next(0);
                                localStorage.removeItem('last-activity-at');
                                this.timeoutConfig.tryLoggingOut$.next(0);
                            }
                        });
                    }
                });
            }
            else {
                console.warn('header::inactivityTimeout::inactivity could not be set because the timeout was less than or equal to 2mins');
            }
        }
        if (!this.timeoutConfig.clickHandlerSub) {
            this.timeoutConfig.clickHandlerSub = fromEvent(document, 'click')
                .pipe(tap(() => localStorage.setItem('last-activity-at', new Date().valueOf().toString())))
                .subscribe(() => {
                this.timeoutConfig.reset$.next(0);
            });
        }
        return {
            countdownTotalInSeconds,
            inactivityDisplay,
            countdown$: this.timeoutConfig.countdown$.asObservable(),
            shouldLogOut$: this.timeoutConfig.tryLoggingOut$.asObservable(),
            subscriptions: [this.timeoutConfig.innerTimerSub, this.timeoutConfig.clickHandlerSub, this.timeoutConfig.activityTimerSub]
        };
    }
    manageAuthenticationState(event) {
        if (event.key === 'logout-catalyst' && event.newValue) {
            console.log('lifecycle-event::logout::a log out event occured on ' + event.newValue + ' causing this application to also trigger it\'s logout event');
            this.logout.emit();
        }
        if (event.key === 'last-activity-at' && event.newValue) {
            if (HeaderComponent.timeoutConfig.activityTimerSub) {
                HeaderComponent.timeoutConfig.reset$.next(0);
            }
        }
    }
    ngOnInit() {
        this.color = this.color;
        if (!this.inactivityTimeout) {
            this.inactivityTimeout = 1000 * 60 * 30;
        }
        if (this.adminPlans.length === 1) {
            this.selectedAdminPlan = this.adminPlans[0].label;
        }
        if (this.dropInConfig?.header?.logo && !this.logo) {
            this.logo = this.dropInConfig.header.logo;
        }
        if (this.dropInConfig?.header?.helpIcon && !this.helpIcon) {
            this.helpIcon = this.dropInConfig.header.helpIcon;
        }
    }
    ngAfterContentInit() {
        if (this.stickyHeader) {
            this.scrollSub = fromEvent(window, 'scroll').pipe(map(x => x.target.scrollingElement.scrollTop), distinctUntilChanged(), catchError(() => { console.warn('Could not determine the scrolling element. Was expecting document'); return of(0); })).subscribe(() => {
                const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
                this.showSticky = hostRect.top + hostRect.height <= 0;
            });
            this.stickyHeader.adminPlans = this.adminPlans;
            this.stickyHeader.planSelected.subscribe(x => this.changeAdminPlan(x));
            this.planSelected.subscribe(x => this.stickyHeader.selectedAdminPlan = x);
            this.stickyHeader.selectedAdminPlan = this.selectedAdminPlan;
            this.stickyHeader.showBanner = this.showBanner;
        }
    }
    ngOnDestroy() {
        [this.scrollSub, ...this.subscriptions].filter(x => !!x).forEach(x => x.unsubscribe());
        HeaderComponent.timeoutConfig = new TimeoutConfig();
    }
    changeAdminPlan(adminPlan) {
        if (this.useAdminPlanWarning) {
            this.nextSelectedAdminPlanDisplay = (this.adminPlans.find(x => x.value === adminPlan)?.label || 'nothing selected').toLowerCase();
            this.selectedAdminPlanDisplay = (this.adminPlans.find(x => x.value === this.selectedAdminPlan)?.label || 'nothing selected').toLowerCase();
            this.nextSelectedAdminPlan = adminPlan;
            this.showAdminPlanWarning = true;
        }
        else {
            this.planSelected.emit(this.selectedAdminPlan = adminPlan);
        }
    }
    cancelAdminPlanChange() {
        of(this.selectedAdminPlan).pipe(delay(0)).subscribe(x => { this.showAdminPlanWarning = false; this.selectedAdminPlan = x; });
        this.selectedAdminPlan = null;
    }
    tryLoggingOut() {
        localStorage.setItem('logout-catalyst', this.config.appName);
        localStorage.setItem('logout-catalyst', '');
        this.logout.emit();
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_showBanner = undefined; }
    static { this.ngAcceptInputType_refreshInterval = undefined; }
    static { this.ngAcceptInputType_inactivityTimeout = undefined; }
    static { this.ngAcceptInputType_useAdminPlanWarning = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderComponent, deps: [{ token: i0.ElementRef }, { token: i1.BasicAuthConfig }, { token: i2.DomSanitizer }, { token: i1.CommonConfig, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: HeaderComponent, isStandalone: true, selector: "rs-header", inputs: { userNameLink: "userNameLink", logo: "logo", helpIcon: "helpIcon", appName: "appName", useAdminPlanWarning: "useAdminPlanWarning", adminPlans: "adminPlans", selectedAdminPlan: "selectedAdminPlan", userName: "userName", inactivityTimeout: "inactivityTimeout", showBanner: "showBanner", color: "color" }, outputs: { planSelected: "planSelected", logout: "logout" }, host: { listeners: { "window:storage": "manageAuthenticationState($event)" } }, queries: [{ propertyName: "stickyHeader", first: true, predicate: StickyHeaderComponent, descendants: true }], ngImport: i0, template: "@if (showBanner) {\n  <rs-banner></rs-banner>\n}\n<header>\n  @if (logo && logo.route) {\n    <a [routerLink]=\"logo.route\" [target]=\"logo.target || '_self'\" class=\"logo\">\n      <img [src]=\"logo.imgSrc\" [alt]=\"logo.text\">\n    </a>\n  }\n  @if (logo && logo.url) {\n    <a [attr.href]=\"logo.url\" [target]=\"logo.target || '_self'\" class=\"logo\">\n      <img [src]=\"logo.imgSrc\" [alt]=\"logo.text\">\n    </a>\n  }\n  <span class=\"app-name\">{{appName}}</span>\n\n  <span class=\"spacer\"></span>\n\n  @if (adminPlans.length === 1) {\n    <span><span [class]=\"$any(adminPlans[0]).image\"></span> {{selectedAdminPlan}}</span>\n  }\n  @if (adminPlans.length > 1) {\n    <rs-select [color]=\"color\"\n      [class]=\"selectedAdminPlan ? ('selected-' + selectedAdminPlan) : ''\" [items]=\"adminPlans\" placeholder=\"Admin Plans\"\n    [selectValue]=\"selectedAdminPlan\" (change)=\"changeAdminPlan($any($event))\"></rs-select>\n  }\n  @if (!userNameLink) {\n    <span class=\"user-name\">{{userName}}</span>\n  }\n  @if (userNameLink && !userNameLink.route && !userNameLink.url) {\n    <span class=\"user-name\">{{userNameLink.text}}</span>\n  }\n  @if (userNameLink && userNameLink.route) {\n    <a [routerLink]=\"userNameLink.route\" class=\"user-name\">{{(userNameLink &&\n    userNameLink.text ? userNameLink.text : userName)}}</a>\n  }\n  @if (userNameLink && userNameLink.url) {\n    <a [target]=\"userNameLink.target\" [href]=\"userNameLink.url\" class=\"user-name\"\n    >{{(userNameLink && userNameLink.text ? userNameLink.text : userName)}}</a>\n  }\n  @if (helpIcon) {\n    <a [href]=\"helpIcon.url\" [target]=\"helpIcon.target\" class=\"support\">\n      <button  title=\"Help articles about this app\" rs-button>\n        <rs-icon [attr.aria-label]=\"helpIcon.text\">{{helpIcon.imgSrc}}</rs-icon>\n      </button>\n    </a>\n  }\n  <button [color]=\"color\" (click)=\"tryLoggingOut()\" rs-outlined-button>Sign Out</button>\n\n</header>\n\n@if (showSticky) {\n  <div class=\"sticky-header\">\n    <ng-content select=\"rs-sticky-header\"></ng-content>\n  </div>\n}\n\n@if (useAdminPlanWarning) {\n  <rs-dialog heading=\"Switching Admin Plans Warning\" [visible]=\"showAdminPlanWarning\"\n    (visibleChange)=\"cancelAdminPlanChange()\">\n    <p class=\"message\">\n      You are trying to switch from <span class=\"message-highlight\">{{selectedAdminPlanDisplay}}</span> to <span\n    class=\"message-highlight\">{{nextSelectedAdminPlanDisplay}}</span>. <br>\n    All of your unsaved work will not be saved. <br>\n    If you have unsaved work that you would like to save please do so before clicking 'Yes' to continue.\n  </p>\n  <div class=\"message-actions\">\n    <button rs-button (click)=\"cancelAdminPlanChange()\">No</button>\n    <button rs-raised-button\n    (click)=\"planSelected.emit(selectedAdminPlan = nextSelectedAdminPlan); showAdminPlanWarning = false;\">Yes</button>\n  </div>\n</rs-dialog>\n}\n\n<rs-dialog heading=\"User Inactivity\" [visible]=\"!!countdown\" (visibleChange)=\"countdown = 0\">\n  <p class=\"message\">You have been inactive for {{inactivityDisplay | date:'mm \\'minute(s)\\' ss\n    \\'second(s)\\''}}. We will auto log you out in {{countdownTotalInSeconds - countdown}} seconds.<br><br>To extend your\n  session click anywhere.</p>\n</rs-dialog>\n\n@if (refreshUrl) {\n  <iframe [src]=\"refreshUrl\"></iframe>\n}\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}header{display:flex;padding:20px 50px 20px 30px;align-items:center;min-height:90px;background-color:#fff}header .logo{border-right:2px gray solid;padding:0 20px}header .logo img{max-height:30px}header .support{text-decoration:none;margin-right:30px;margin-left:-16px}header .support button{padding:0;min-width:24px;display:flex}header .spacer{flex:1}header .app-name{padding:0 20px;font-size:1.75em}header .user-name{padding:0 30px}header rs-select{min-width:160px}.sticky-header{position:sticky;top:0;width:100%;height:auto;align-self:flex-start;float:right;z-index:2}.sticky-header:empty{display:none}iframe{height:0;width:0;border:none;visibility:hidden;position:absolute}.message{padding:30px;text-align:center}.message .message-highlight{font-weight:700}.message-actions{padding-bottom:30px;text-align:center}:host ::ng-deep rs-select>.form-group{margin-bottom:4px!important}\n"], dependencies: [{ kind: "component", type: BannerComponent, selector: "rs-banner" }, { kind: "component", type: SelectComponent, selector: "rs-select", inputs: ["autocomplete", "label", "placeholder", "validation", "errors", "appendTo", "hideErrorMessage", "id", "multiple", "searchable", "clearable", "items", "selectValue", "tabindex", "disabled", "color"], outputs: ["change", "close"] }, { kind: "component", type: ButtonComponent, selector: "button[rs-button], button[rs-raised-button], button[rs-outlined-button]", inputs: ["color"] }, { kind: "component", type: IconComponent, selector: "rs-icon" }, { kind: "component", type: DialogComponent, selector: "rs-dialog", inputs: ["origin", "baseZIndex", "hideOverlay", "canMove", "canResize", "visible", "heading", "maxWidth"], outputs: ["visibleChange"] }, { kind: "pipe", type: DatePipe, name: "date" }, { kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-header', standalone: true, imports: [BannerComponent, SelectComponent, ButtonComponent, IconComponent, DialogComponent, DatePipe, RouterLink], template: "@if (showBanner) {\n  <rs-banner></rs-banner>\n}\n<header>\n  @if (logo && logo.route) {\n    <a [routerLink]=\"logo.route\" [target]=\"logo.target || '_self'\" class=\"logo\">\n      <img [src]=\"logo.imgSrc\" [alt]=\"logo.text\">\n    </a>\n  }\n  @if (logo && logo.url) {\n    <a [attr.href]=\"logo.url\" [target]=\"logo.target || '_self'\" class=\"logo\">\n      <img [src]=\"logo.imgSrc\" [alt]=\"logo.text\">\n    </a>\n  }\n  <span class=\"app-name\">{{appName}}</span>\n\n  <span class=\"spacer\"></span>\n\n  @if (adminPlans.length === 1) {\n    <span><span [class]=\"$any(adminPlans[0]).image\"></span> {{selectedAdminPlan}}</span>\n  }\n  @if (adminPlans.length > 1) {\n    <rs-select [color]=\"color\"\n      [class]=\"selectedAdminPlan ? ('selected-' + selectedAdminPlan) : ''\" [items]=\"adminPlans\" placeholder=\"Admin Plans\"\n    [selectValue]=\"selectedAdminPlan\" (change)=\"changeAdminPlan($any($event))\"></rs-select>\n  }\n  @if (!userNameLink) {\n    <span class=\"user-name\">{{userName}}</span>\n  }\n  @if (userNameLink && !userNameLink.route && !userNameLink.url) {\n    <span class=\"user-name\">{{userNameLink.text}}</span>\n  }\n  @if (userNameLink && userNameLink.route) {\n    <a [routerLink]=\"userNameLink.route\" class=\"user-name\">{{(userNameLink &&\n    userNameLink.text ? userNameLink.text : userName)}}</a>\n  }\n  @if (userNameLink && userNameLink.url) {\n    <a [target]=\"userNameLink.target\" [href]=\"userNameLink.url\" class=\"user-name\"\n    >{{(userNameLink && userNameLink.text ? userNameLink.text : userName)}}</a>\n  }\n  @if (helpIcon) {\n    <a [href]=\"helpIcon.url\" [target]=\"helpIcon.target\" class=\"support\">\n      <button  title=\"Help articles about this app\" rs-button>\n        <rs-icon [attr.aria-label]=\"helpIcon.text\">{{helpIcon.imgSrc}}</rs-icon>\n      </button>\n    </a>\n  }\n  <button [color]=\"color\" (click)=\"tryLoggingOut()\" rs-outlined-button>Sign Out</button>\n\n</header>\n\n@if (showSticky) {\n  <div class=\"sticky-header\">\n    <ng-content select=\"rs-sticky-header\"></ng-content>\n  </div>\n}\n\n@if (useAdminPlanWarning) {\n  <rs-dialog heading=\"Switching Admin Plans Warning\" [visible]=\"showAdminPlanWarning\"\n    (visibleChange)=\"cancelAdminPlanChange()\">\n    <p class=\"message\">\n      You are trying to switch from <span class=\"message-highlight\">{{selectedAdminPlanDisplay}}</span> to <span\n    class=\"message-highlight\">{{nextSelectedAdminPlanDisplay}}</span>. <br>\n    All of your unsaved work will not be saved. <br>\n    If you have unsaved work that you would like to save please do so before clicking 'Yes' to continue.\n  </p>\n  <div class=\"message-actions\">\n    <button rs-button (click)=\"cancelAdminPlanChange()\">No</button>\n    <button rs-raised-button\n    (click)=\"planSelected.emit(selectedAdminPlan = nextSelectedAdminPlan); showAdminPlanWarning = false;\">Yes</button>\n  </div>\n</rs-dialog>\n}\n\n<rs-dialog heading=\"User Inactivity\" [visible]=\"!!countdown\" (visibleChange)=\"countdown = 0\">\n  <p class=\"message\">You have been inactive for {{inactivityDisplay | date:'mm \\'minute(s)\\' ss\n    \\'second(s)\\''}}. We will auto log you out in {{countdownTotalInSeconds - countdown}} seconds.<br><br>To extend your\n  session click anywhere.</p>\n</rs-dialog>\n\n@if (refreshUrl) {\n  <iframe [src]=\"refreshUrl\"></iframe>\n}\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}header{display:flex;padding:20px 50px 20px 30px;align-items:center;min-height:90px;background-color:#fff}header .logo{border-right:2px gray solid;padding:0 20px}header .logo img{max-height:30px}header .support{text-decoration:none;margin-right:30px;margin-left:-16px}header .support button{padding:0;min-width:24px;display:flex}header .spacer{flex:1}header .app-name{padding:0 20px;font-size:1.75em}header .user-name{padding:0 30px}header rs-select{min-width:160px}.sticky-header{position:sticky;top:0;width:100%;height:auto;align-self:flex-start;float:right;z-index:2}.sticky-header:empty{display:none}iframe{height:0;width:0;border:none;visibility:hidden;position:absolute}.message{padding:30px;text-align:center}.message .message-highlight{font-weight:700}.message-actions{padding-bottom:30px;text-align:center}:host ::ng-deep rs-select>.form-group{margin-bottom:4px!important}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.BasicAuthConfig }, { type: i2.DomSanitizer }, { type: i1.CommonConfig, decorators: [{
                    type: Optional
                }] }], propDecorators: { planSelected: [{
                type: Output
            }], logout: [{
                type: Output
            }], userNameLink: [{
                type: Input
            }], logo: [{
                type: Input
            }], helpIcon: [{
                type: Input
            }], appName: [{
                type: Input
            }], useAdminPlanWarning: [{
                type: Input
            }], adminPlans: [{
                type: Input
            }], selectedAdminPlan: [{
                type: Input
            }], userName: [{
                type: Input
            }], inactivityTimeout: [{
                type: Input
            }], showBanner: [{
                type: Input
            }], color: [{
                type: Input
            }], stickyHeader: [{
                type: ContentChild,
                args: [StickyHeaderComponent]
            }], manageAuthenticationState: [{
                type: HostListener,
                args: ['window:storage', ['$event']]
            }] } });

class HeaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, imports: [CommonModule,
            RouterModule,
            SelectModule,
            ButtonModule,
            DialogModule,
            IconModule,
            BannerModule,
            HeaderComponent, StickyHeaderComponent], exports: [HeaderComponent,
            StickyHeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, imports: [CommonModule,
            RouterModule,
            SelectModule,
            ButtonModule,
            DialogModule,
            IconModule,
            BannerModule,
            HeaderComponent, StickyHeaderComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        SelectModule,
                        ButtonModule,
                        DialogModule,
                        IconModule,
                        BannerModule,
                        HeaderComponent, StickyHeaderComponent
                    ],
                    exports: [
                        HeaderComponent,
                        StickyHeaderComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { HeaderComponent, HeaderModule, StickyHeaderComponent, TimeoutConfig };
